import React, { useCallback, useState } from 'react'
import { VStack } from '@musinsa/mwms-ds'
import { FormProvider, useForm } from 'react-hook-form'
import { flushSync } from 'react-dom'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { omitBy } from 'lodash-es'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import OutboundSearchBatchForm, { getInitForm } from '@/pages/outbound/outboundSearchBatch/components/OutboundSearchBatchForm'
import { GetOutboundSummaryReq, OutboundSummaryItem } from '@/services/outbound/outboundSearch/types'
import ResizableContents from '@/components/content/ResizableContents'
import { OutboundSearchBatchTable } from '@/pages/outbound/outboundSearchBatch/components/OutboundSearchBatchTable'
import { OutboundSearchBatchDetailTable } from '@/pages/outbound/outboundSearchBatch/components/OutboundSearchBatchDetailTable'
import { useCancelOutboundCutoff, useGetOutbounds, useExportExcelOnOutbounds, useOutboundSummary } from '@/services/outbound/outboundSearch/useOutboundSearchQuries'
import { GRID_SIZE } from '@/utils/grid/grid'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import MToast from '@/utils/modal/MToast'
import { Utils } from '@/utils/Utils'
import { MENU_CODE } from './constants'

function OutboundSearchBatch() {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<GetOutboundSummaryReq & { isShipperNamesSelectedAll?: boolean }>()

  const form = useForm<GetOutboundSummaryReq>({
    defaultValues: getInitForm(parsedQs),
  })

  const [queryKey, setQueryKey] = useState(form.getValues())
  const [currentPage, setCurrentPage] = useState(0)
  const [detailCurrentPage, setDetailCurrentPage] = useState(0)
  const [summaryItem, setSummaryItem] = useState<OutboundSummaryItem | undefined>(undefined)

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } =
    useShipperFieldSelection<GetOutboundSummaryReq>({
      logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
      shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
    })

  const { mutateAsync: cancelCutoff } = useCancelOutboundCutoff()

  const { data, refetch, isFetching } = useOutboundSummary({ ...queryKey, page: currentPage, size: GRID_SIZE.MEDIUM })
  const {
    data: detailData,
    refetch: detailRefetch,
    isFetching: isDetailFetching,
  } = useGetOutbounds({ ...queryKey, page: detailCurrentPage, size: GRID_SIZE.MEDIUM, ...summaryItem }, MENU_CODE)
  const { onExportData } = useExportExcelOnOutbounds(
    {
      ...parsedQs,
      ...summaryItem,
      shipperNames: detailData?.items[0]?.shipperName ? [detailData.items[0].shipperName] : [],
      logisticsServiceTypes: detailData?.items[0]?.logisticsServiceType ? [detailData.items[0].logisticsServiceType] : [],
      page: currentPage,
      size: GRID_SIZE.MEDIUM,
    },
    MENU_CODE,
  )

  const handleReset = useCallback(() => {
    form.reset(getInitForm())
    resetShipperSelection()
  }, [form, resetShipperSelection])

  const handleSubmit = useCallback(
    async (formData: GetOutboundSummaryReq) => {
      const processedData = processShipperDataForSubmit(formData)
      const query = omitBy(processedData, (value) => value === '') as GetOutboundSummaryReq
      MenuStorage.menuOnSession.setSearch({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      flushSync(() => {
        setQueryKey(query)
        setCurrentPage(0)
      })

      reload()
      await refetch()

      replaceQuery({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [processShipperDataForSubmit, reload, refetch, replaceQuery],
  )

  const onChangePage = useCallback(
    async (page: number) => {
      flushSync(() => setCurrentPage(page))

      await refetch()
    },
    [refetch],
  )

  const onChangeDetailPage = useCallback(
    async (page: number) => {
      flushSync(() => setDetailCurrentPage(page))

      await detailRefetch()
    },
    [detailRefetch],
  )

  const onDetailSearch = async (item: OutboundSummaryItem) => {
    flushSync(() => setDetailCurrentPage(0))

    setSummaryItem(item)
  }

  const handleCancelClick = async (item: OutboundSummaryItem) => {
    try {
      await cancelCutoff(item.cutOffNo)
      MToast.show('출고가 취소되었습니다.')
      await refetch()
    } catch (error) {
      MToast.error(Utils.getApiErrMessage(error, '출고 취소에 실패했습니다.'))
    }
  }

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="출고조회 (차수별)" />
          <OutboundSearchBatchForm onSubmit={handleSubmit} isLoading={isFetching} onFieldChange={handleShipperFieldChange} onReset={handleReset} />
        </VStack>
        <ResizableContents
          list={[
            {
              content: (
                <OutboundSearchBatchTable
                  items={data?.items || []}
                  pageInfo={data?.pageInfo}
                  isLoading={isFetching}
                  currentPage={currentPage}
                  onChangePage={onChangePage}
                  onDetailSearch={onDetailSearch}
                  onCancelClick={handleCancelClick}
                />
              ),
            },
            {
              content: (
                <OutboundSearchBatchDetailTable
                  items={detailData?.items || []}
                  pageInfo={detailData?.pageInfo}
                  isLoading={isDetailFetching}
                  currentPage={detailCurrentPage}
                  onChangePage={onChangeDetailPage}
                  onExportData={onExportData}
                />
              ),
            },
          ]}
        />
      </PageLayout>
    </FormProvider>
  )
}

export default OutboundSearchBatch
